/**
 * Enersee Design System
 *
 * Inspired by modern design system documentation (Pulse, Heartbeat)
 * A comprehensive showcase of the Enersee design system tokens
 *
 * DO NOT use this component in production - it's for reference only.
 */

import React from 'react';
import { colorTokens } from '../../lib/tokens/colors';
import { spacingTokens } from '../../lib/tokens/spacing';
import { radiusTokens } from '../../lib/tokens/radius';
import { typographyTokens } from '../../lib/tokens/typography';

// Construct tokens object locally
const tokens = {
  colors: colorTokens,
  spacing: spacingTokens,
  radius: radiusTokens,
  typography: typographyTokens,
};

export const EnerseeDesignSystem: React.FC = () => {
  const [inputValue, setInputValue] = React.useState('');
  const [isChecked, setIsChecked] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState('introduction');

  const scrollToSection = (sectionId: string): void => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: tokens.colors.background.primary,
      }}
    >
      {/* Sidebar Navigation - Inspired by Pulse */}
      <nav
        style={{
          width: '280px',
          position: 'fixed',
          height: '100vh',
          backgroundColor: tokens.colors.background.secondary,
          borderRight: `1px solid ${tokens.colors.border.secondary}`,
          padding: tokens.spacing['2xl'],
          overflowY: 'auto',
        }}
      >
        <div style={{ marginBottom: tokens.spacing['4xl'] }}>
          <h2
            style={{
              fontSize: tokens.typography.fontSize.xl,
              fontWeight: tokens.typography.fontWeight.semibold,
              color: tokens.colors.text.primary,
              marginBottom: tokens.spacing.xs,
            }}
          >
            Design System
          </h2>
          <p
            style={{
              fontSize: tokens.typography.fontSize.sm,
              color: tokens.colors.text.tertiary,
            }}
          >
            Enersee Platform 2.0
          </p>
        </div>

        <div>
          {[
            { id: 'introduction', label: 'Introduction' },
            { id: 'principles', label: 'Design Principles' },
            { id: 'colors', label: 'Colors' },
            { id: 'typography', label: 'Typography' },
            { id: 'spacing', label: 'Spacing' },
            { id: 'radius', label: 'Border Radius' },
            { id: 'components', label: 'Components' },
            { id: 'buttons', label: 'Buttons' },
            { id: 'forms', label: 'Forms' },
            { id: 'cards', label: 'Cards' },
            { id: 'badges', label: 'Badges' },
            { id: 'alerts', label: 'Alerts' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => {
                scrollToSection(item.id);
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: `${tokens.spacing.md} ${tokens.spacing.lg}`,
                marginBottom: tokens.spacing.xs,
                backgroundColor:
                  activeSection === item.id
                    ? tokens.colors.background['brand-primary']
                    : 'transparent',
                color:
                  activeSection === item.id
                    ? tokens.colors.text['brand-primary']
                    : tokens.colors.text.secondary,
                border: 'none',
                borderRadius: tokens.radius.md,
                fontSize: tokens.typography.fontSize.md,
                fontWeight:
                  activeSection === item.id
                    ? tokens.typography.fontWeight.semibold
                    : tokens.typography.fontWeight.regular,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (activeSection !== item.id) {
                  e.currentTarget.style.backgroundColor =
                    tokens.colors.background.tertiary;
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== item.id) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main
        style={{
          marginLeft: '280px',
          flex: 1,
          padding: tokens.spacing['6xl'],
          maxWidth: '1400px',
        }}
      >
        {/* Hero Section - Inspired by Pulse */}
        <section
          id="introduction"
          style={{
            marginBottom: tokens.spacing['8xl'],
            paddingBottom: tokens.spacing['6xl'],
            borderBottom: `1px solid ${tokens.colors.border.secondary}`,
          }}
        >
          <h1
            style={{
              fontSize: '48px',
              fontWeight: tokens.typography.fontWeight.semibold,
              lineHeight: '1.2',
              color: tokens.colors.text.primary,
              marginBottom: tokens.spacing.xl,
            }}
          >
            Building great products
            <br />
            <span style={{ color: tokens.colors.text['brand-primary'] }}>
              with consistent design
            </span>
          </h1>

          <p
            style={{
              fontSize: tokens.typography.fontSize.xl,
              color: tokens.colors.text.tertiary,
              marginBottom: tokens.spacing['4xl'],
              maxWidth: '720px',
              lineHeight: '1.6',
            }}
          >
            Our design system provides a unified language and consistent
            components to help teams build better products faster.
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: tokens.spacing.xl,
            }}
          >
            <InfoCard
              icon="🎨"
              title="Design Tokens"
              description="Colors, spacing, typography and other fundamental building blocks"
            />
            <InfoCard
              icon="🧩"
              title="Components"
              description="Reusable UI components built with our design tokens"
            />
            <InfoCard
              icon="📐"
              title="Best Practices"
              description="Guidelines and patterns for consistent implementation"
            />
          </div>
        </section>

        {/* Design Principles */}
        <section
          id="principles"
          style={{
            marginBottom: tokens.spacing['8xl'],
          }}
        >
          <SectionHeader
            title="Design Principles"
            description="The core values that guide our design decisions"
          />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: tokens.spacing['2xl'],
            }}
          >
            <PrincipleCard
              number="01"
              title="Consistency"
              description="Use design tokens consistently across all products to create a unified experience."
            />
            <PrincipleCard
              number="02"
              title="Accessibility"
              description="Ensure all components meet WCAG guidelines and are usable by everyone."
            />
            <PrincipleCard
              number="03"
              title="Simplicity"
              description="Keep interfaces clean and focused on user needs without unnecessary complexity."
            />
            <PrincipleCard
              number="04"
              title="Flexibility"
              description="Design systems should adapt to different contexts while maintaining coherence."
            />
          </div>
        </section>

        {/* Color System */}
        <section
          id="colors"
          style={{
            marginBottom: tokens.spacing['8xl'],
          }}
        >
          <SectionHeader
            title="Colors"
            description="Our color palette is carefully crafted to ensure accessibility and brand consistency"
          />

          {/* Brand Colors */}
          <div style={{ marginBottom: tokens.spacing['4xl'] }}>
            <SubsectionHeader title="Brand Colors" />
            <p
              style={{
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.text.tertiary,
                marginBottom: tokens.spacing.xl,
              }}
            >
              Primary brand colors that define our visual identity
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: tokens.spacing.md,
              }}
            >
              <ColorSwatch
                label="Brand Primary"
                bg={tokens.colors.background['brand-primary']}
                text={tokens.colors.text['brand-primary']}
                border={tokens.colors.border.brand}
              />
              <ColorSwatch
                label="Brand Secondary"
                bg={tokens.colors.background['brand-secondary']}
                text={tokens.colors.text['brand-secondary']}
                border={tokens.colors.border['brand-alt']}
              />
              <ColorSwatch
                label="Brand Solid"
                bg={tokens.colors.background['brand-solid']}
                text={tokens.colors.text['primary-on-brand']}
              />
              <ColorSwatch
                label="Brand Section"
                bg={tokens.colors.background['brand-section']}
                text={tokens.colors.text['primary-on-brand']}
              />
            </div>
          </div>

          {/* Utility Colors */}
          <div style={{ marginBottom: tokens.spacing['4xl'] }}>
            <SubsectionHeader title="Utility Colors" />
            <p
              style={{
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.text.tertiary,
                marginBottom: tokens.spacing.xl,
              }}
            >
              Semantic colors for success, warning, and error states
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: tokens.spacing.md,
              }}
            >
              <ColorSwatch
                label="Success"
                bg={tokens.colors.background['success-primary']}
                text={tokens.colors.text['success-primary']}
              />
              <ColorSwatch
                label="Success Solid"
                bg={tokens.colors.background['success-solid']}
                text={tokens.colors.base.white}
              />
              <ColorSwatch
                label="Warning"
                bg={tokens.colors.background['warning-primary']}
                text={tokens.colors.text['warning-primary']}
              />
              <ColorSwatch
                label="Warning Solid"
                bg={tokens.colors.background['warning-solid']}
                text={tokens.colors.base.white}
              />
              <ColorSwatch
                label="Error"
                bg={tokens.colors.background['error-primary']}
                text={tokens.colors.text['error-primary']}
              />
              <ColorSwatch
                label="Error Solid"
                bg={tokens.colors.background['error-solid']}
                text={tokens.colors.base.white}
              />
            </div>
          </div>

          {/* Neutral Colors */}
          <div>
            <SubsectionHeader title="Neutral & Background Colors" />
            <p
              style={{
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.text.tertiary,
                marginBottom: tokens.spacing.xl,
              }}
            >
              Versatile neutral colors for backgrounds, borders, and text
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: tokens.spacing.md,
              }}
            >
              <ColorSwatch
                label="Primary"
                bg={tokens.colors.background.primary}
                text={tokens.colors.text.primary}
                border={tokens.colors.border.primary}
              />
              <ColorSwatch
                label="Secondary"
                bg={tokens.colors.background.secondary}
                text={tokens.colors.text.secondary}
                border={tokens.colors.border.secondary}
              />
              <ColorSwatch
                label="Tertiary"
                bg={tokens.colors.background.tertiary}
                text={tokens.colors.text.tertiary}
                border={tokens.colors.border.tertiary}
              />
              <ColorSwatch
                label="Disabled"
                bg={tokens.colors.background.disabled}
                text={tokens.colors.text.disabled}
                border={tokens.colors.border.disabled}
              />
            </div>
          </div>
        </section>

        {/* Typography */}
        <section
          id="typography"
          style={{
            marginBottom: tokens.spacing['8xl'],
          }}
        >
          <SectionHeader
            title="Typography"
            description="A clear typographic hierarchy that ensures readability and visual harmony"
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: tokens.spacing['2xl'],
            }}
          >
            {Object.entries(tokens.typography.textStyles).map(
              ([key, style]) => (
                <div
                  key={key}
                  style={{
                    padding: tokens.spacing.xl,
                    backgroundColor: tokens.colors.background.secondary,
                    borderRadius: tokens.radius.md,
                    borderLeft: `4px solid ${tokens.colors.border.brand}`,
                  }}
                >
                  <p style={style}>
                    {key
                      .replace(/-/g, ' ')
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </p>
                  <p
                    style={{
                      fontSize: tokens.typography.fontSize.xs,
                      color: tokens.colors.text.tertiary,
                      marginTop: tokens.spacing.sm,
                    }}
                  >
                    Size: {style.fontSize} / Weight: {style.fontWeight} / Line
                    Height: {style.lineHeight}
                  </p>
                </div>
              )
            )}
          </div>
        </section>

        {/* Spacing */}
        <section
          id="spacing"
          style={{
            marginBottom: tokens.spacing['8xl'],
          }}
        >
          <SectionHeader
            title="Spacing"
            description="Consistent spacing creates visual rhythm and improves scanability"
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: tokens.spacing.lg,
            }}
          >
            {Object.entries(tokens.spacing).map(([key, value]) => (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: tokens.spacing.xl,
                }}
              >
                <div
                  style={{
                    width: '100px',
                    fontSize: tokens.typography.fontSize.sm,
                    fontWeight: tokens.typography.fontWeight.medium,
                    color: tokens.colors.text.secondary,
                  }}
                >
                  {key}
                </div>
                <div
                  style={{
                    height: '32px',
                    width: value as string,
                    backgroundColor: tokens.colors.background['brand-solid'],
                    borderRadius: tokens.radius.xs as string,
                  }}
                />
                <div
                  style={{
                    fontSize: tokens.typography.fontSize.sm,
                    color: tokens.colors.text.tertiary,
                  }}
                >
                  {value}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Border Radius */}
        <section
          id="radius"
          style={{
            marginBottom: tokens.spacing['8xl'],
          }}
        >
          <SectionHeader
            title="Border Radius"
            description="Rounded corners that add softness and friendliness to our interface"
          />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: tokens.spacing.xl,
            }}
          >
            {Object.entries(tokens.radius).map(([key, value]) => (
              <div
                key={key}
                style={{
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100px',
                    backgroundColor:
                      tokens.colors.background['brand-secondary'],
                    borderRadius: value as string,
                    marginBottom: tokens.spacing.md,
                    border: `2px solid ${tokens.colors.border.brand}`,
                  }}
                />
                <div
                  style={{
                    fontSize: tokens.typography.fontSize.sm,
                    fontWeight: tokens.typography.fontWeight.medium,
                    color: tokens.colors.text.primary,
                  }}
                >
                  {key}
                </div>
                <div
                  style={{
                    fontSize: tokens.typography.fontSize.xs,
                    color: tokens.colors.text.tertiary,
                  }}
                >
                  {value}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Components Section */}
        <section
          id="components"
          style={{
            marginBottom: tokens.spacing['6xl'],
          }}
        >
          <SectionHeader
            title="Components"
            description="Reusable building blocks built with our design tokens"
          />
        </section>

        {/* Buttons */}
        <section
          id="buttons"
          style={{
            marginBottom: tokens.spacing['8xl'],
          }}
        >
          <SubsectionHeader title="Buttons" />

          <div style={{ marginBottom: tokens.spacing['4xl'] }}>
            <p
              style={{
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.text.tertiary,
                marginBottom: tokens.spacing.xl,
              }}
            >
              Primary buttons for main actions
            </p>
            <div
              style={{
                display: 'flex',
                gap: tokens.spacing.md,
                flexWrap: 'wrap',
              }}
            >
              <button
                style={{
                  backgroundColor: tokens.colors.background['brand-solid'],
                  color: tokens.colors.text['primary-on-brand'],
                  padding: `${tokens.spacing.md} ${tokens.spacing.xl}`,
                  borderRadius: tokens.radius.md,
                  border: 'none',
                  fontSize: tokens.typography.fontSize.md,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  cursor: 'pointer',
                }}
              >
                Primary Button
              </button>
              <button
                style={{
                  backgroundColor:
                    tokens.colors.background['brand-solid-hover'],
                  color: tokens.colors.text['primary-on-brand'],
                  padding: `${tokens.spacing.md} ${tokens.spacing.xl}`,
                  borderRadius: tokens.radius.md,
                  border: 'none',
                  fontSize: tokens.typography.fontSize.md,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  cursor: 'pointer',
                }}
              >
                Primary Hover
              </button>
              <button
                style={{
                  backgroundColor: tokens.colors.background.disabled,
                  color: tokens.colors.text.disabled,
                  padding: `${tokens.spacing.md} ${tokens.spacing.xl}`,
                  borderRadius: tokens.radius.md,
                  border: 'none',
                  fontSize: tokens.typography.fontSize.md,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  cursor: 'not-allowed',
                }}
                disabled
              >
                Disabled
              </button>
            </div>
          </div>

          <div style={{ marginBottom: tokens.spacing['4xl'] }}>
            <p
              style={{
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.text.tertiary,
                marginBottom: tokens.spacing.xl,
              }}
            >
              Secondary buttons for less prominent actions
            </p>
            <div
              style={{
                display: 'flex',
                gap: tokens.spacing.md,
                flexWrap: 'wrap',
              }}
            >
              <button
                style={{
                  backgroundColor: 'transparent',
                  color: tokens.colors.text['brand-primary'],
                  padding: `${tokens.spacing.md} ${tokens.spacing.xl}`,
                  borderRadius: tokens.radius.md,
                  border: `2px solid ${tokens.colors.border.brand}`,
                  fontSize: tokens.typography.fontSize.md,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  cursor: 'pointer',
                }}
              >
                Secondary Button
              </button>
              <button
                style={{
                  backgroundColor: tokens.colors.background['brand-primary'],
                  color: tokens.colors.text['brand-primary'],
                  padding: `${tokens.spacing.md} ${tokens.spacing.xl}`,
                  borderRadius: tokens.radius.md,
                  border: `2px solid ${tokens.colors.border.brand}`,
                  fontSize: tokens.typography.fontSize.md,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  cursor: 'pointer',
                }}
              >
                Secondary Hover
              </button>
            </div>
          </div>

          <div>
            <p
              style={{
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.text.tertiary,
                marginBottom: tokens.spacing.xl,
              }}
            >
              Semantic buttons for success, warning, and error actions
            </p>
            <div
              style={{
                display: 'flex',
                gap: tokens.spacing.md,
                flexWrap: 'wrap',
              }}
            >
              <button
                style={{
                  backgroundColor: tokens.colors.background['success-solid'],
                  color: tokens.colors.base.white,
                  padding: `${tokens.spacing.md} ${tokens.spacing.xl}`,
                  borderRadius: tokens.radius.md,
                  border: 'none',
                  fontSize: tokens.typography.fontSize.md,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  cursor: 'pointer',
                }}
              >
                Success
              </button>
              <button
                style={{
                  backgroundColor: tokens.colors.background['warning-solid'],
                  color: tokens.colors.base.white,
                  padding: `${tokens.spacing.md} ${tokens.spacing.xl}`,
                  borderRadius: tokens.radius.md,
                  border: 'none',
                  fontSize: tokens.typography.fontSize.md,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  cursor: 'pointer',
                }}
              >
                Warning
              </button>
              <button
                style={{
                  backgroundColor: tokens.colors.background['error-solid'],
                  color: tokens.colors.base.white,
                  padding: `${tokens.spacing.md} ${tokens.spacing.xl}`,
                  borderRadius: tokens.radius.md,
                  border: 'none',
                  fontSize: tokens.typography.fontSize.md,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  cursor: 'pointer',
                }}
              >
                Error/Danger
              </button>
            </div>
          </div>
        </section>

        {/* Forms */}
        <section
          id="forms"
          style={{
            marginBottom: tokens.spacing['8xl'],
          }}
        >
          <SubsectionHeader title="Form Elements" />
          <p
            style={{
              fontSize: tokens.typography.fontSize.sm,
              color: tokens.colors.text.tertiary,
              marginBottom: tokens.spacing.xl,
            }}
          >
            Consistent form inputs for data collection
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: tokens.spacing.xl,
            }}
          >
            {/* Text Input */}
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: tokens.typography.fontSize.sm,
                  fontWeight: tokens.typography.fontWeight.medium,
                  color: tokens.colors.text.secondary,
                  marginBottom: tokens.spacing.xs,
                }}
              >
                Text Input
              </label>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                }}
                placeholder="Enter text..."
                style={{
                  width: '100%',
                  padding: tokens.spacing.md,
                  borderRadius: tokens.radius.md,
                  border: `1px solid ${tokens.colors.border.primary}`,
                  fontSize: tokens.typography.fontSize.md,
                  color: tokens.colors.text.primary,
                  backgroundColor: tokens.colors.background.primary,
                }}
              />
            </div>

            {/* Select */}
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: tokens.typography.fontSize.sm,
                  fontWeight: tokens.typography.fontWeight.medium,
                  color: tokens.colors.text.secondary,
                  marginBottom: tokens.spacing.xs,
                }}
              >
                Select
              </label>
              <select
                style={{
                  width: '100%',
                  padding: tokens.spacing.md,
                  borderRadius: tokens.radius.md,
                  border: `1px solid ${tokens.colors.border.primary}`,
                  fontSize: tokens.typography.fontSize.md,
                  color: tokens.colors.text.primary,
                  backgroundColor: tokens.colors.background.primary,
                }}
              >
                <option>Option 1</option>
                <option>Option 2</option>
                <option>Option 3</option>
              </select>
            </div>

            {/* Checkbox */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: tokens.spacing.md,
                  fontSize: tokens.typography.fontSize.md,
                  color: tokens.colors.text.primary,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    setIsChecked(e.target.checked);
                  }}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                  }}
                />
                Checkbox Label
              </label>
            </div>
          </div>
        </section>

        {/* Cards */}
        <section
          id="cards"
          style={{
            marginBottom: tokens.spacing['8xl'],
          }}
        >
          <SubsectionHeader title="Cards" />
          <p
            style={{
              fontSize: tokens.typography.fontSize.sm,
              color: tokens.colors.text.tertiary,
              marginBottom: tokens.spacing.xl,
            }}
          >
            Container components for grouping related content
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: tokens.spacing.xl,
            }}
          >
            <CardExample
              title="Default Card"
              description="This is a default card with primary background"
              bg={tokens.colors.background.primary}
              border={tokens.colors.border.primary}
            />
            <CardExample
              title="Brand Card"
              description="This card uses brand colors"
              bg={tokens.colors.background['brand-primary']}
              border={tokens.colors.border.brand}
            />
            <CardExample
              title="Success Card"
              description="This card indicates success"
              bg={tokens.colors.background['success-primary']}
              border={tokens.colors.foreground['success-primary']}
            />
          </div>
        </section>

        {/* Badges */}
        <section
          id="badges"
          style={{
            marginBottom: tokens.spacing['8xl'],
          }}
        >
          <SubsectionHeader title="Badges & Chips" />
          <p
            style={{
              fontSize: tokens.typography.fontSize.sm,
              color: tokens.colors.text.tertiary,
              marginBottom: tokens.spacing.xl,
            }}
          >
            Small status indicators and labels
          </p>

          <div
            style={{
              display: 'flex',
              gap: tokens.spacing.md,
              flexWrap: 'wrap',
            }}
          >
            <Badge label="Default" bg={tokens.colors.background.secondary} />
            <Badge
              label="Brand"
              bg={tokens.colors.background['brand-secondary']}
            />
            <Badge
              label="Success"
              bg={tokens.colors.background['success-secondary']}
            />
            <Badge
              label="Warning"
              bg={tokens.colors.background['warning-secondary']}
            />
            <Badge
              label="Error"
              bg={tokens.colors.background['error-secondary']}
            />
          </div>
        </section>

        {/* Alerts */}
        <section
          id="alerts"
          style={{
            marginBottom: tokens.spacing['8xl'],
          }}
        >
          <SubsectionHeader title="Alerts & Notifications" />
          <p
            style={{
              fontSize: tokens.typography.fontSize.sm,
              color: tokens.colors.text.tertiary,
              marginBottom: tokens.spacing.xl,
            }}
          >
            Contextual feedback messages for users
          </p>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: tokens.spacing.md,
            }}
          >
            <Alert
              type="success"
              title="Success"
              message="Your action was completed successfully."
            />
            <Alert
              type="warning"
              title="Warning"
              message="Please review your changes before proceeding."
            />
            <Alert
              type="error"
              title="Error"
              message="An error occurred. Please try again."
            />
            <Alert
              type="info"
              title="Information"
              message="Here's some helpful information for you."
            />
          </div>
        </section>

        {/* Footer */}
        <footer
          style={{
            marginTop: tokens.spacing['10xl'],
            paddingTop: tokens.spacing['6xl'],
            borderTop: `1px solid ${tokens.colors.border.secondary}`,
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontSize: tokens.typography.fontSize.md,
              color: tokens.colors.text.tertiary,
              marginBottom: tokens.spacing.md,
            }}
          >
            Need help with design & development?
          </p>
          <p
            style={{
              fontSize: tokens.typography.fontSize.lg,
              fontWeight: tokens.typography.fontWeight.semibold,
              color: tokens.colors.text['brand-primary'],
            }}
          >
            Learn more about Enersee Platform →
          </p>
        </footer>
      </main>
    </div>
  );
};

// ==================== Helper Components ====================

const SectionHeader: React.FC<{ title: string; description: string }> = ({
  title,
  description,
}) => (
  <div style={{ marginBottom: tokens.spacing['4xl'] }}>
    <h2
      style={{
        fontSize: '32px',
        fontWeight: tokens.typography.fontWeight.semibold,
        color: tokens.colors.text.primary,
        marginBottom: tokens.spacing.md,
      }}
    >
      {title}
    </h2>
    <p
      style={{
        fontSize: tokens.typography.fontSize.lg,
        color: tokens.colors.text.tertiary,
        maxWidth: '720px',
      }}
    >
      {description}
    </p>
  </div>
);

const SubsectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h3
    style={{
      fontSize: tokens.typography.fontSize.xl,
      fontWeight: tokens.typography.fontWeight.semibold,
      color: tokens.colors.text.secondary,
      marginBottom: tokens.spacing.lg,
    }}
  >
    {title}
  </h3>
);

const InfoCard: React.FC<{
  icon: string;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <div
    style={{
      padding: tokens.spacing.xl,
      backgroundColor: tokens.colors.background.secondary,
      borderRadius: tokens.radius.xl,
      border: `1px solid ${tokens.colors.border.secondary}`,
    }}
  >
    <div
      style={{
        fontSize: '32px',
        marginBottom: tokens.spacing.md,
      }}
    >
      {icon}
    </div>
    <h3
      style={{
        fontSize: tokens.typography.fontSize.lg,
        fontWeight: tokens.typography.fontWeight.semibold,
        color: tokens.colors.text.primary,
        marginBottom: tokens.spacing.sm,
      }}
    >
      {title}
    </h3>
    <p
      style={{
        fontSize: tokens.typography.fontSize.md,
        color: tokens.colors.text.tertiary,
        lineHeight: '1.5',
      }}
    >
      {description}
    </p>
  </div>
);

const PrincipleCard: React.FC<{
  number: string;
  title: string;
  description: string;
}> = ({ number, title, description }) => (
  <div
    style={{
      padding: tokens.spacing['2xl'],
      backgroundColor: tokens.colors.background.primary,
      borderRadius: tokens.radius.xl,
      border: `1px solid ${tokens.colors.border.primary}`,
    }}
  >
    <div
      style={{
        fontSize: tokens.typography.fontSize.xl,
        fontWeight: tokens.typography.fontWeight.semibold,
        color: tokens.colors.text['brand-primary'],
        marginBottom: tokens.spacing.md,
      }}
    >
      {number}
    </div>
    <h4
      style={{
        fontSize: tokens.typography.fontSize.lg,
        fontWeight: tokens.typography.fontWeight.semibold,
        color: tokens.colors.text.primary,
        marginBottom: tokens.spacing.sm,
      }}
    >
      {title}
    </h4>
    <p
      style={{
        fontSize: tokens.typography.fontSize.md,
        color: tokens.colors.text.tertiary,
        lineHeight: '1.6',
      }}
    >
      {description}
    </p>
  </div>
);

const ColorSwatch: React.FC<{
  label: string;
  bg: string;
  text: string;
  border?: string;
}> = ({ label, bg, text, border }) => (
  <div
    style={{
      backgroundColor: bg,
      color: text,
      padding: tokens.spacing.xl,
      borderRadius: tokens.radius.md,
      border: border
        ? `1px solid ${border}`
        : `1px solid ${tokens.colors.border.secondary}`,
      textAlign: 'center',
      fontSize: tokens.typography.fontSize.sm,
      fontWeight: tokens.typography.fontWeight.medium,
      minHeight: '80px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {label}
  </div>
);

const CardExample: React.FC<{
  title: string;
  description: string;
  bg: string;
  border: string;
}> = ({ title, description, bg, border }) => (
  <div
    style={{
      backgroundColor: bg,
      padding: tokens.spacing.xl,
      borderRadius: tokens.radius.xl,
      border: `1px solid ${border}`,
    }}
  >
    <h3
      style={{
        fontSize: tokens.typography.fontSize.lg,
        fontWeight: tokens.typography.fontWeight.semibold,
        color: tokens.colors.text.primary,
        marginBottom: tokens.spacing.md,
      }}
    >
      {title}
    </h3>
    <p
      style={{
        fontSize: tokens.typography.fontSize.md,
        color: tokens.colors.text.tertiary,
        marginBottom: tokens.spacing.lg,
      }}
    >
      {description}
    </p>
    <button
      style={{
        backgroundColor: tokens.colors.background['brand-solid'],
        color: tokens.colors.text['primary-on-brand'],
        padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
        borderRadius: tokens.radius.sm,
        border: 'none',
        fontSize: tokens.typography.fontSize.sm,
        fontWeight: tokens.typography.fontWeight.medium,
        cursor: 'pointer',
      }}
    >
      Action
    </button>
  </div>
);

const Badge: React.FC<{ label: string; bg: string }> = ({ label, bg }) => (
  <span
    style={{
      backgroundColor: bg,
      color: tokens.colors.text.primary,
      padding: `${tokens.spacing.xs} ${tokens.spacing.md}`,
      borderRadius: tokens.radius.full,
      fontSize: tokens.typography.fontSize.xs,
      fontWeight: tokens.typography.fontWeight.medium,
      display: 'inline-block',
    }}
  >
    {label}
  </span>
);

const Alert: React.FC<{
  type: 'success' | 'warning' | 'error' | 'info';
  title: string;
  message: string;
}> = ({ type, title, message }) => {
  const colorMap = {
    success: {
      bg: tokens.colors.background['success-primary'],
      border: tokens.colors.foreground['success-primary'],
      text: tokens.colors.text['success-primary'],
    },
    warning: {
      bg: tokens.colors.background['warning-primary'],
      border: tokens.colors.foreground['warning-primary'],
      text: tokens.colors.text['warning-primary'],
    },
    error: {
      bg: tokens.colors.background['error-primary'],
      border: tokens.colors.foreground['error-primary'],
      text: tokens.colors.text['error-primary'],
    },
    info: {
      bg: tokens.colors.background['brand-primary'],
      border: tokens.colors.border.brand,
      text: tokens.colors.text['brand-primary'],
    },
  };

  const colors = colorMap[type];

  return (
    <div
      style={{
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: tokens.radius.md,
        padding: tokens.spacing.lg,
      }}
    >
      <h4
        style={{
          fontSize: tokens.typography.fontSize.md,
          fontWeight: tokens.typography.fontWeight.semibold,
          color: colors.text,
          marginBottom: tokens.spacing.xs,
        }}
      >
        {title}
      </h4>
      <p
        style={{
          fontSize: tokens.typography.fontSize.sm,
          color: colors.text,
        }}
      >
        {message}
      </p>
    </div>
  );
};

export default EnerseeDesignSystem;
